/**
 * TimeFrameGateConfig — attach a time frame to an IVR node and
 * configure the "off-hours" behavior.
 *
 * As of migration 0078, time frames are a universal gate on ANY
 * node type. When the frame's evaluator says we're not in the
 * frame's set of moments, the interpreter short-circuits the node:
 *
 *   1. Optionally plays a "we're closed" recording.
 *   2. Runs the configured action:
 *        - hunt_back     (default) pop back to the parent / fallback
 *        - hangup        end the call
 *        - route_to_node go to a specific node id
 *
 * Component name kept as `TimeFramePicker` for import-path stability —
 * an earlier iteration was just the frame select. The richer config
 * lives behind the same name to avoid touching consumers.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { timeFrames, formatTimeFrameRef } from '@/api/timeFrames';
import type { IvrNodeRow } from '@/api/ivrNodes';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Clock, ExternalLink, AlertCircle } from 'lucide-react';
import { RecordingPicker } from './RecordingPicker';
import { NodeTypeBadge } from './NodeTypeBadge';

const NONE_VALUE = '__none__';

type TimeFrameAction = 'hunt_back' | 'hangup' | 'route_to_node';

export interface TimeFrameGateValue {
  time_frame_id: string | null;
  time_frame_action: TimeFrameAction | null;
  time_frame_target_node_id: string | null;
  time_frame_recording_id: string | null;
}

export function TimeFramePicker({
  value,
  onChange,
  treeNodes,
  currentNodeId,
}: {
  value: TimeFrameGateValue;
  onChange: (next: TimeFrameGateValue) => void;
  /** All nodes in the current tree, for the "route to node" dropdown. */
  treeNodes: IvrNodeRow[];
  /** The current node's id, excluded from the target dropdown. */
  currentNodeId?: string;
}) {
  const customerId = useEffectiveCustomerId();
  const { data, isLoading } = useQuery({
    queryKey: ['timeFrames', 'picker', customerId],
    queryFn: () => timeFrames.list({ customer_id: customerId ?? undefined }),
  });
  const frames = data?.rows ?? [];
  const selected = frames.find((r) => r.id === value.time_frame_id) ?? null;
  const action: TimeFrameAction = value.time_frame_action ?? 'hunt_back';

  // Helpers to mutate one piece of the value at a time.
  const setFrame = (id: string | null) => onChange({ ...value, time_frame_id: id });
  const setAction = (a: TimeFrameAction) => onChange({ ...value, time_frame_action: a });
  const setTarget = (id: string | null) => onChange({ ...value, time_frame_target_node_id: id });
  const setRecording = (id: string | null) => onChange({ ...value, time_frame_recording_id: id });

  // Empty state — customer has no frames yet.
  if (!isLoading && frames.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-secondary/20 p-3 space-y-1.5">
        <p className="text-xs text-muted-foreground">
          No time frames have been created yet for this customer.
        </p>
        <Link
          to="/time-frames"
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          Manage time frames
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Frame picker */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground block">
          Time frame
        </label>
        <Select
          value={value.time_frame_id ?? NONE_VALUE}
          onValueChange={(v) => setFrame(v === NONE_VALUE ? null : v)}
        >
          <SelectTrigger className="w-full h-8 text-sm">
            <SelectValue placeholder="No time frame attached" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_VALUE}>
              <span className="text-muted-foreground">— None (no time gate) —</span>
            </SelectItem>
            {frames.map((r) => (
              <SelectItem key={r.id} value={r.id}>
                <span className="inline-flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5 opacity-60" />
                  <span>{r.name}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">
                    {formatTimeFrameRef(r.ref_number)}
                  </span>
                  {!r.active && <Badge variant="secondary" className="text-[10px]">inactive</Badge>}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selected && (
          <p className="text-[11px] text-muted-foreground leading-snug">
            When inside the frame, this node runs normally. When outside —
            the off-hours behavior below kicks in.{' '}
            <Link
              to={`/time-frames/${formatTimeFrameRef(selected.ref_number)}`}
              className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-0.5"
            >
              Edit frame <ExternalLink className="h-2.5 w-2.5" />
            </Link>
          </p>
        )}
      </div>

      {/* Off-hours behavior — only meaningful when a frame is attached */}
      {value.time_frame_id && (
        <>
          <div className="space-y-1.5 pt-2 border-t border-border">
            <label className="text-xs font-medium text-muted-foreground block">
              When NOT in frame — do this
            </label>
            <Select value={action} onValueChange={(v) => setAction(v as TimeFrameAction)}>
              <SelectTrigger className="w-full h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hunt_back">
                  <span className="inline-flex flex-col items-start text-left">
                    <span>Hunt back to parent</span>
                    <span className="text-[10px] text-muted-foreground">
                      Pop back to the node that brought us here (default).
                    </span>
                  </span>
                </SelectItem>
                <SelectItem value="hangup">
                  <span className="inline-flex flex-col items-start text-left">
                    <span>Hang up</span>
                    <span className="text-[10px] text-muted-foreground">
                      End the call cleanly.
                    </span>
                  </span>
                </SelectItem>
                <SelectItem value="route_to_node">
                  <span className="inline-flex flex-col items-start text-left">
                    <span>Route to a specific node</span>
                    <span className="text-[10px] text-muted-foreground">
                      Advance to another IVR node (e.g. an after-hours menu).
                    </span>
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Target node — conditional */}
          {action === 'route_to_node' && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground block">
                Target node
              </label>
              <Select
                value={value.time_frame_target_node_id ?? ''}
                onValueChange={(v) => setTarget(v || null)}
              >
                <SelectTrigger className="w-full h-8 text-sm">
                  <SelectValue placeholder="Pick a target node…" />
                </SelectTrigger>
                <SelectContent>
                  {treeNodes
                    .filter((n) => n.id !== currentNodeId)
                    .map((n) => (
                      <SelectItem key={n.id} value={n.id}>
                        <span className="flex items-center gap-2">
                          <span className="font-mono text-[11px] text-slate-500">
                            IVR-{String(n.ref_number)}
                          </span>
                          <span>{n.name}</span>
                          <NodeTypeBadge type={n.node_type} />
                        </span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {!value.time_frame_target_node_id && (
                <p className="inline-flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400">
                  <AlertCircle className="h-3 w-3" />
                  Pick a target — otherwise the gate falls back to hunt-back.
                </p>
              )}
            </div>
          )}

          {/* Optional recording */}
          <div className="space-y-1.5 pt-2 border-t border-border">
            <label className="text-xs font-medium text-muted-foreground block">
              Off-hours recording (optional)
            </label>
            <RecordingPicker
              label=""
              value={value.time_frame_recording_id}
              onChange={setRecording}
            />
            <p className="text-[11px] text-muted-foreground leading-snug">
              Plays once before the action runs. Typical use: a
              "we're closed, please call back during business hours"
              announcement before hanging up or routing.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
